/**
 * Phase 640 — Video Analysis Studio (annotation canvas overlay)
 *
 * Renders the video with an absolutely-positioned SVG overlay that
 * lets the coach draw:
 *   - arrows (click-drag)
 *   - circles (click-drag)
 *   - text labels (click, then type in the input that appears)
 *
 * Each annotation is timestamped to the video's currentTime when the
 * coach starts drawing. Playback shows only annotations whose
 * [start_sec, end_sec] window contains the current time.
 *
 * Annotations are stored in the parent component's state as
 * plain JSON so they can be persisted via PATCH /video-analyses/{id}.
 */
import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Play, Pause, ArrowUpRight, Circle, Type, Undo2, Trash2, Palette } from 'lucide-react';

const COLORS = ['#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#a855f7', '#ffffff'];
const ANNOTATION_WINDOW = 3.0; // seconds — how long each annotation stays on screen

export default function VideoAnnotationStudio({
  videoSrc,
  annotations = [],
  onChange,
  readonly = false,
  onDurationChange,
}) {
  const videoRef = useRef(null);
  const svgRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [tool, setTool] = useState('arrow'); // arrow | circle | text
  const [color, setColor] = useState('#ef4444');
  const [drawing, setDrawing] = useState(null); // { kind, startX, startY, endX, endY }
  const [textPending, setTextPending] = useState(null); // { x, y }
  const [textValue, setTextValue] = useState('');

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onTime = () => setCurrentTime(v.currentTime);
    const onDur = () => {
      setDuration(v.duration || 0);
      onDurationChange?.(v.duration || 0);
    };
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    v.addEventListener('timeupdate', onTime);
    v.addEventListener('loadedmetadata', onDur);
    v.addEventListener('play', onPlay);
    v.addEventListener('pause', onPause);
    return () => {
      v.removeEventListener('timeupdate', onTime);
      v.removeEventListener('loadedmetadata', onDur);
      v.removeEventListener('play', onPlay);
      v.removeEventListener('pause', onPause);
    };
  }, [onDurationChange]);

  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play(); else v.pause();
  }, []);

  const seek = useCallback((sec) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = Math.max(0, Math.min(duration || 0, sec));
  }, [duration]);

  // Convert client (x,y) → svg viewbox (0-100, 0-100) normalised coordinates
  // so annotations stay pinned to the video content regardless of container size.
  const clientToNorm = useCallback((e) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const rect = svg.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    };
  }, []);

  const handleMouseDown = useCallback((e) => {
    if (readonly) return;
    const { x, y } = clientToNorm(e);
    if (tool === 'text') {
      setTextPending({ x, y });
      setTextValue('');
      videoRef.current?.pause();
      return;
    }
    videoRef.current?.pause();
    setDrawing({ kind: tool, startX: x, startY: y, endX: x, endY: y });
  }, [tool, clientToNorm, readonly]);

  const handleMouseMove = useCallback((e) => {
    if (!drawing) return;
    const { x, y } = clientToNorm(e);
    setDrawing({ ...drawing, endX: x, endY: y });
  }, [drawing, clientToNorm]);

  const handleMouseUp = useCallback(() => {
    if (!drawing) return;
    // Ignore tiny movements — treat as a mis-click.
    const dx = drawing.endX - drawing.startX;
    const dy = drawing.endY - drawing.startY;
    if (Math.sqrt(dx * dx + dy * dy) < 0.5) {
      setDrawing(null);
      return;
    }
    const t = videoRef.current?.currentTime || 0;
    const shape = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      kind: drawing.kind,
      color,
      start_sec: Math.max(0, t - 0.1),
      end_sec: Math.max(0, t - 0.1) + ANNOTATION_WINDOW,
      geometry: {
        x1: drawing.startX, y1: drawing.startY,
        x2: drawing.endX, y2: drawing.endY,
      },
    };
    onChange?.([...annotations, shape]);
    setDrawing(null);
  }, [drawing, color, annotations, onChange]);

  const commitText = useCallback(() => {
    if (!textPending || !textValue.trim()) {
      setTextPending(null);
      setTextValue('');
      return;
    }
    const t = videoRef.current?.currentTime || 0;
    const shape = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      kind: 'text',
      color,
      start_sec: Math.max(0, t - 0.1),
      end_sec: Math.max(0, t - 0.1) + ANNOTATION_WINDOW,
      geometry: { x1: textPending.x, y1: textPending.y, caption: textValue.trim() },
    };
    onChange?.([...annotations, shape]);
    setTextPending(null);
    setTextValue('');
  }, [textPending, textValue, color, annotations, onChange]);

  const removeLast = useCallback(() => {
    if (!annotations.length) return;
    onChange?.(annotations.slice(0, -1));
  }, [annotations, onChange]);

  const removeAll = useCallback(() => {
    if (!annotations.length) return;
    if (!window.confirm('Remove all annotations? This cannot be undone.')) return;
    onChange?.([]);
  }, [annotations, onChange]);

  // Only render annotations whose time window contains currentTime.
  const visible = useMemo(() => (
    (annotations || []).filter(a =>
      (a.start_sec ?? 0) <= currentTime && (a.end_sec ?? 0) >= currentTime
    )
  ), [annotations, currentTime]);

  return (
    <div className="space-y-3" data-testid="video-annotation-studio">
      {/* Video + overlay */}
      <div className="relative rounded-lg overflow-hidden bg-black aspect-video max-h-[60vh]">
        {videoSrc ? (
          <video ref={videoRef} src={videoSrc} className="w-full h-full object-contain"
                 crossOrigin="anonymous" playsInline
                 data-testid="video-player" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/60 text-sm">
            No video loaded
          </div>
        )}
        <svg
          ref={svgRef}
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          className={`absolute inset-0 w-full h-full ${readonly ? 'pointer-events-none' : 'cursor-crosshair'}`}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          data-testid="annotation-svg"
        >
          <defs>
            {COLORS.map(c => (
              <marker key={`mk-${c}`} id={`arrow-${c.slice(1)}`} viewBox="0 0 10 10" refX="8" refY="5"
                      markerWidth="3" markerHeight="3" orient="auto-start-reverse">
                <path d="M0,0 L10,5 L0,10 z" fill={c} />
              </marker>
            ))}
          </defs>
          {/* Committed annotations, timestamp-filtered */}
          {visible.map(a => renderShape(a))}
          {/* In-progress preview */}
          {drawing && renderShape({ ...drawing, kind: drawing.kind, color, geometry: { x1: drawing.startX, y1: drawing.startY, x2: drawing.endX, y2: drawing.endY } })}
        </svg>
        {/* In-place text-entry input */}
        {textPending && (
          <div
            className="absolute z-10"
            style={{ left: `${textPending.x}%`, top: `${textPending.y}%`, transform: 'translate(-50%, -50%)' }}
          >
            <div className="flex items-center gap-1 bg-white rounded-md shadow-lg p-1 border">
              <Input autoFocus value={textValue} onChange={e => setTextValue(e.target.value)}
                     onKeyDown={e => { if (e.key === 'Enter') commitText(); if (e.key === 'Escape') { setTextPending(null); setTextValue(''); } }}
                     className="h-7 text-xs w-40" placeholder="Type label…"
                     data-testid="annotation-text-input" />
              <Button size="sm" onClick={commitText} className="h-7 text-xs"
                      data-testid="annotation-text-save">Add</Button>
            </div>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <Button size="sm" variant="outline" onClick={togglePlay} data-testid="video-play-toggle">
          {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
        </Button>
        <input
          type="range" min={0} max={duration || 0} step={0.1} value={currentTime}
          onChange={e => seek(parseFloat(e.target.value))}
          className="flex-1 h-1 accent-primary"
          data-testid="video-scrubber"
        />
        <span className="font-mono text-slate-500 min-w-[80px] text-right">
          {formatTime(currentTime)} / {formatTime(duration)}
        </span>
      </div>

      {/* Tool row */}
      {!readonly && (
        <div className="flex flex-wrap items-center gap-2">
          <ToolBtn active={tool === 'arrow'} onClick={() => setTool('arrow')} icon={ArrowUpRight} label="Arrow" testid="tool-arrow" />
          <ToolBtn active={tool === 'circle'} onClick={() => setTool('circle')} icon={Circle} label="Circle" testid="tool-circle" />
          <ToolBtn active={tool === 'text'} onClick={() => setTool('text')} icon={Type} label="Text" testid="tool-text" />
          <div className="flex items-center gap-1 ml-2 pl-2 border-l">
            <Palette className="h-3.5 w-3.5 text-slate-500" />
            {COLORS.map(c => (
              <button key={c}
                      onClick={() => setColor(c)}
                      className={`w-5 h-5 rounded-full border-2 transition-transform ${color === c ? 'scale-110 border-slate-900' : 'border-white'}`}
                      style={{ background: c }}
                      title={c}
                      aria-label={`Color ${c}`}
                      data-testid={`color-${c.slice(1)}`} />
            ))}
          </div>
          <div className="ml-auto flex items-center gap-1">
            <Button size="sm" variant="ghost" onClick={removeLast} disabled={!annotations.length}
                    data-testid="annotation-undo">
              <Undo2 className="h-3.5 w-3.5 mr-1" /> Undo
            </Button>
            <Button size="sm" variant="ghost" onClick={removeAll} disabled={!annotations.length}
                    className="text-red-600 hover:bg-red-50" data-testid="annotation-clear">
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Clear
            </Button>
          </div>
        </div>
      )}

      {/* Live annotation counter */}
      <p className="text-[10px] text-slate-500">
        {annotations.length} annotation{annotations.length === 1 ? '' : 's'}. Each stays on-screen for {ANNOTATION_WINDOW}s from when you drew it.
      </p>
    </div>
  );
}

function ToolBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button onClick={onClick}
            className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs border transition-colors ${active ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-slate-200 hover:border-primary/50'}`}
            data-testid={testid}>
      <Icon className="h-3.5 w-3.5" /> {label}
    </button>
  );
}

function renderShape(a) {
  const g = a.geometry || {};
  if (a.kind === 'arrow') {
    return (
      <line key={a.id} x1={g.x1} y1={g.y1} x2={g.x2} y2={g.y2}
            stroke={a.color} strokeWidth={1.2}
            markerEnd={`url(#arrow-${(a.color || '#ef4444').slice(1)})`}
            vectorEffect="non-scaling-stroke" />
    );
  }
  if (a.kind === 'circle') {
    const cx = (g.x1 + g.x2) / 2;
    const cy = (g.y1 + g.y2) / 2;
    const rx = Math.abs(g.x2 - g.x1) / 2;
    const ry = Math.abs(g.y2 - g.y1) / 2;
    return (
      <ellipse key={a.id} cx={cx} cy={cy} rx={rx} ry={ry}
               stroke={a.color} strokeWidth={1.2} fill="none"
               vectorEffect="non-scaling-stroke" />
    );
  }
  if (a.kind === 'text') {
    return (
      <text key={a.id} x={g.x1} y={g.y1} fontSize="3" fontFamily="Inter, sans-serif"
            fill={a.color} textAnchor="middle" dominantBaseline="middle"
            stroke="rgba(0,0,0,0.75)" strokeWidth={0.25} paintOrder="stroke fill"
            style={{ pointerEvents: 'none' }}>
        {g.caption}
      </text>
    );
  }
  return null;
}

function formatTime(sec) {
  const s = Math.max(0, sec || 0);
  const m = Math.floor(s / 60);
  const rs = Math.floor(s % 60);
  return `${m}:${String(rs).padStart(2, '0')}`;
}
